import React, { memo } from 'react'
import { Trans } from 'react-i18next'
import { Link } from 'react-router-dom'

import Text from '../shared/text/Text'
import CustomAccordion from '../shared/customAccordion/CustomAccordion'
import BaseCard from '../shared/baseCard/BaseCard'
import {
  PRIVACY_POLICY_URL,
  TERMS_CONDITIONS_URL
} from '../../constants/navigation'
import { floorNumber, formatEth } from '../../util/numbers.helpers'
import { formatTicks } from '../../util/string.helpers'

const FAQs = ({
  t,
  mainProfile: { preSaleDepositPrice, publicSaleDepositPrice }
}) => {
  const exampleDepositValue = 2
  const preRaffleDeposited = floorNumber(
    exampleDepositValue / preSaleDepositPrice
  )
  const publicRaffleDeposited = floorNumber(
    exampleDepositValue / publicSaleDepositPrice
  )
  const data = [
    {
      title: 'How does the QBX Public Community Sale raffle system work?',
      description: (
        <>
          <p>
            The QBX Public Community Sale is based on a raffle system with two
            different ticket types (pre-sale tickets and public-sale tickets)
            that you receive based on the amount of ETH that you deposit.
          </p>
          <p>
            You receive both pre-sale and public-sale tickets when you deposit
            ETH, given the amount of ETH that you deposit is enough for the
            price of the tickets.
          </p>
          <p>
            Once the token sale deposit phase is over, the raffle tickets go
            through a randomized picking process where the winning tickets are
            drawn.
          </p>
          <p>
            Your winning tickets will give you a QBX allocation and for your
            tickets that did not win, you can withdraw your deposited ETH.{' '}
            <strong>
              Example: You deposit 5 ETH and after the raffle ends, let&apos;s
              say you win an allocation of 2ETH in $QBX. This gets you:
            </strong>
          </p>
          <ul>
            <li>
              <strong>2 ETH in $QBX (congratulations!)</strong>
            </li>
            <li>
              <strong>
                3 ETH that didn&apos;t win, which you can withdraw{' '}
                <strong>around 48 hours after the public sale ends</strong>
              </strong>
            </li>
            <li>
              <strong>Deposit points for your rewards based on 5 ETH</strong>
            </li>
          </ul>
        </>
      )
    },
    {
      title: 'How do I participate in QBX Public Community Sale?',
      description: (
        <>
          <p>
            It is very easy to participate in the QBX Public Community Sale, all
            you have to do is the following:
          </p>
          <ol type='1'>
            <li>Connect your wallet via the blue button</li>
            <li>
              Deposit as many ETH as you wish for the desired amount of raffle
              tickets
            </li>
            <li>
              The more you deposit, the more tickets you get, and the higher
              your chance of winning a QBX allocation
            </li>
          </ol>
        </>
      )
    },
    {
      title: 'What is the min. and max. deposit I have to make to participate?',
      description: (
        <>
          <p>
            There is no minimum or maximum you have to deposit. Although, to be
            able to participate in the raffle to win a QBX allocation, you have
            to at least deposit enough ETH to get 1 public-sale raffle ticket.
          </p>
          <p>
            The more raffle tickets you get, the higher your chances of winning
            a QBX allocation.
          </p>
          <p>
            The price of a pre-sale ticket is {formatEth(preSaleDepositPrice)},
            and the price of a public-sale ticket is{' '}
            {formatEth(publicSaleDepositPrice)}. So for example: if you deposit{' '}
            {exampleDepositValue} ETH, you will get{' '}
            {formatTicks(preRaffleDeposited)} pre-sale tickets and{' '}
            {formatTicks(publicRaffleDeposited)} public-sale tickets.
          </p>
        </>
      )
    },
    {
      title: 'How many QBX do I get per ticket?',
      description: (
        <p>
          For each of your bought <strong>pre-sale tickets</strong> that wins,
          you <strong>get 31&apos;089 QBX</strong>, and for each of your bought{' '}
          <strong>public-sale tickets</strong> that wins, you{' '}
          <strong>get 2&apos;000 QBX</strong>.
        </p>
      )
    },
    {
      title: 'What are Deposit Points?',
      description: (
        <>
          <p>
            When you deposit ETH to get QBX Public Community Sale raffle
            tickets, you are rewarded with Deposit Points (or Pts.). You earn 10
            Deposit Points for every $1 that you deposit. The best part,
            regardless of whether your bought raffle tickets win or not, you
            still get to keep all your earned Deposit Points!
          </p>
          <p>
            <strong>So for example:</strong> if you deposit $1&apos;000 worth of
            ETH to get raffle tickets, you will earn a minimum of 10&apos;000
            Pts.
          </p>
          <p>
            It says a &quot;minimum&quot; because you can get extra multipliers
            and bonuses that enable you to earn even more points! Click on the
            &quot;Points bonuses&quot; link under the deposit button to view all
            the multipliers and bonuses you can get.
          </p>
        </>
      )
    },
    {
      title: 'How does the reward pool work?',
      description: (
        <>
          <p>
            The size of the reward pool depends on the total amount deposited by
            all QBX Public Community Sale participants, up to $1.25 million in
            rewards.
          </p>
          <p>In short, the more ETH deposited = a bigger reward pool.</p>
          <p>
            When the raffle has been drawn and the results are out, you will be
            able to redeem all your earned Deposit Points into VIR that can be
            used in the QBX VIP Club to get crypto (via Binance), cash, gift
            cards (+600 gift cards from Netflix, Amazon, Ikea, etc.) or products
            (+1&apos;000 brands like Apple and Adidas).
          </p>
        </>
      )
    },
    {
      title: 'What special benefits do QBX Founders Key holders get?',
      description: (
        <>
          <p>
            If you hold one or more QBX Founders Keys you get the following
            benefits from participating in the QBX Public Community Sale:
          </p>
          <ol>
            <li>
              For every key that you hold you get 1 pre-sale ticket that is
              guaranteed to win the raffle, given you deposit the necessary ETH
              to buy it. <strong>So for example:</strong> You hold 3 keys and
              buy 5 pre-sale tickets, 3 of those 5 pre-sale tickets are
              guaranteed to win and give you the QBX allocation, and the other 2
              go into the raffle drawing process.
            </li>
            <li>
              You get an exclusive Deposit Points multiplier based on the amount
              of keys that you hold. These are the multiplier amounts:
            </li>
          </ol>
          <ol type='a' style={{ paddingLeft: 60 }}>
            <li>
              You hold QBX Founders Key ≥ 20 = <strong>25x multiplier</strong>
            </li>
            <li>
              You hold QBX Founders Key ≥ 10 = <strong>12x multiplier</strong>
            </li>
            <li>
              You hold QBX Founders Key ≥ 3 = <strong>6x multiplier</strong>
            </li>
            <li>
              You hold QBX Founders Key ≥ 1 = <strong>3x multiplier</strong>
            </li>
          </ol>
        </>
      )
    },
    {
      title: 'What happens when the QBX Public Community Sale is over?',
      description: (
        <ol type='1'>
          <li>
            Once the count down timer goes to 0, the token sale deposit phase is
            over.
          </li>
          <li>
            After that, the randomized picking process phase begins where the
            winning raffle tickets are drawn.
          </li>
          <li>
            When the raffle tickets that won have been drawn, you are able to go
            to the results page to see if you won, withdraw the ETH for your
            tickets that did not win, and redeem your Deposit Points.
          </li>
        </ol>
      )
    },
    {
      title: 'What deposit security measures are implemented?',
      description: (
        <p>
          In order to ensure that the deposited ETH is secure and untouchable by
          anyone, the QBX Public Community Sale deposits all go to a smart
          contract that was thoroughly audited by Certik.
        </p>
      )
    },
    {
      title: 'Terms of the QBX Public Community Sale',
      customDesc: (
        <Text
          label={
            <Trans i18nKey='home.privacy-policy-desc'>
              <Link to={TERMS_CONDITIONS_URL} style={{ color: 'inherit' }} />
              <Link to={PRIVACY_POLICY_URL} style={{ color: 'inherit' }} />
            </Trans>
          }
          sx={{ color: 'inherit' }}
        />
      )
    }
  ]
  return (
    <BaseCard id='faqs'>
      <Text
        variant='h2'
        label={t('home.faqs')}
        fontWeight={700}
        sx={{ mb: 2 }}
      />
      <CustomAccordion data={data} />
    </BaseCard>
  )
}

export default memo(FAQs)
